import { useEffect, useState } from 'react';

const SIDEBAR_TOGGLE_EVENT = 'mfe-filter-bar-toggle';
const SIDEBAR_HOST_SELECTOR = 'content-menu, content-metrics-menu';

const isHostVisible = (host: Element): boolean => {
  if (typeof host.checkVisibility === 'function' && !host.checkVisibility()) return false;
  if (getComputedStyle(host).display === 'contents') return true;
  return host.getBoundingClientRect().width > 0;
};

// Returns undefined when no sidebar host exists, so the toggle event stays the source of truth.
const readSidebarHostState = (): boolean | undefined => {
  const hosts = Array.from(document.querySelectorAll(SIDEBAR_HOST_SELECTOR));
  if (hosts.length === 0) return undefined;
  return hosts.some(isHostVisible);
};

/**
 * Tracks whether the sidebar is open. It listens to the toggle event and also reads the real
 * sidebar host from the DOM, so the state is correct after a page reload with a collapsed sidebar
 * (when the toggle event is never fired).
 */
export const useSidebarOpen = (): boolean => {
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    let frame = 0;

    const syncFromDom = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const state = readSidebarHostState();
        if (state !== undefined) setIsOpen(state);
      });
    };

    const handleToggle = (event: Event) => {
      setIsOpen((event as CustomEvent<{ isVisible?: boolean }>).detail?.isVisible ?? true);
    };

    const observer = new MutationObserver(syncFromDom);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden'],
    });
    document.addEventListener(SIDEBAR_TOGGLE_EVENT, handleToggle);
    globalThis.addEventListener('resize', syncFromDom);
    syncFromDom();

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      document.removeEventListener(SIDEBAR_TOGGLE_EVENT, handleToggle);
      globalThis.removeEventListener('resize', syncFromDom);
    };
  }, []);

  return isOpen;
};
